import { useState } from "react";
import { addDays, format, isSameDay, isToday as isDateToday } from "date-fns";
import { ptBR } from "date-fns/locale";
import { Calendar as CalendarIcon, ChevronDown } from "lucide-react";
import { Calendar, Popover, PopoverContent, PopoverTrigger, Sheet, SheetContent, SheetHeader, SheetTitle } from "@bookdental/ui";
import { useIsMobile } from "@/lib/use-media-query";
import { AJUDA_AGENDA } from "@/lib/ajuda/dialogos/agenda";

/** Seletor de data moderno — botão com formatação rica + popover com calendar
 *  shadcn + atalhos (Hoje / Amanhã / +7 dias). Preserva o formato YYYY-MM-DD
 *  usado pelo restante do dialog. */
export function DatePickerButton({
  value,
  onChange,
}: {
  value: string;
  onChange: (ymd: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const isMobile = useIsMobile();
  // Parse YYYY-MM-DD local (evita shift de timezone do new Date("YYYY-MM-DD"))
  const parsed = (() => {
    if (!value) return new Date();
    const [y, m, d] = value.split("-").map(Number);
    return new Date(y ?? 0, (m ?? 1) - 1, d ?? 1);
  })();

  const todayDate = new Date();
  const tomorrow = addDays(todayDate, 1);
  const plusSeven = addDays(todayDate, 7);

  const dayLabel = format(parsed, "EEE", { locale: ptBR });
  const mainLabel = isDateToday(parsed)
    ? "Hoje"
    : isSameDay(parsed, tomorrow)
      ? "Amanhã"
      : format(parsed, "dd 'de' MMM", { locale: ptBR });
  const yearLabel = format(parsed, "yyyy", { locale: ptBR });

  const toYmd = (d: Date) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
      d.getDate(),
    ).padStart(2, "0")}`;

  const escolher = (d: Date | undefined) => {
    if (!d) return;
    onChange(toYmd(d));
    setOpen(false);
  };

  const conteudo = (
    <>
      {/* Atalhos */}
      <div className="flex gap-1 border-b border-zinc-100 bg-zinc-50/50 px-2 py-2 dark:border-border dark:bg-muted/20">
        <QuickDate label="Hoje" date={todayDate} active={isSameDay(parsed, todayDate)} onPick={escolher} />
        <QuickDate label="Amanhã" date={tomorrow} active={isSameDay(parsed, tomorrow)} onPick={escolher} />
        <QuickDate label="+7 dias" date={plusSeven} active={isSameDay(parsed, plusSeven)} onPick={escolher} />
      </div>

      {/* Calendar shadcn */}
      <Calendar mode="single" selected={parsed} onSelect={escolher} weekStartsOn={0} initialFocus />
    </>
  );

  const visualDoGatilho = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-brand-50 text-brand-600 transition-colors group-hover:bg-brand-100 dark:bg-primary/10 dark:text-primary dark:group-hover:bg-primary/20">
        <CalendarIcon className="h-4 w-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-[13px] font-semibold leading-tight text-zinc-900 dark:text-foreground">
          {mainLabel}
        </span>
        <span className="block truncate text-[10px] font-medium uppercase leading-tight tracking-wider text-zinc-500 dark:text-muted-foreground">
          {dayLabel} · {yearLabel}
        </span>
      </span>
      <ChevronDown className="h-3.5 w-3.5 shrink-0 text-zinc-400 transition-transform group-data-[state=open]:rotate-180 dark:text-muted-foreground" />
    </>
  );
  const gatilhoCls =
    "group flex w-full items-center gap-2 rounded-md text-left transition-colors hover:bg-zinc-50 dark:hover:bg-muted/40";

  // Celular: folha inferior, o padrão do app — popover ancorado nunca vence o Safari do iPhone
  // (a barra de endereço flutuante cobre a base e o conteúdo fica cortado sem rolagem alcançável).
  if (isMobile) {
    return (
      <>
        <button type="button" onClick={() => setOpen(true)} className={gatilhoCls}>
          {visualDoGatilho}
        </button>
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent
            side="bottom"
            className="max-h-[85dvh] gap-0 overflow-y-auto overscroll-contain rounded-t-2xl border-zinc-200 bg-white p-0 dark:border-border dark:bg-card"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
          >
            <SheetHeader className="border-b border-zinc-100 px-4 py-3 text-left dark:border-border">
              <SheetTitle className="text-sm font-bold text-zinc-900 dark:text-foreground" ajuda={AJUDA_AGENDA.escolherData}>
                Escolher data
              </SheetTitle>
            </SheetHeader>
            <div className="[&>div:last-child]:mx-auto [&>div:last-child]:w-fit">{conteudo}</div>
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" className={gatilhoCls}>
          {visualDoGatilho}
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={8}
        collisionPadding={12}
        style={{ maxHeight: "var(--radix-popover-content-available-height)" }}
        className="w-auto overflow-y-auto overscroll-contain rounded-xl border border-zinc-200 bg-white p-0 shadow-xl dark:border-border dark:bg-card"
      >
        {conteudo}
      </PopoverContent>
    </Popover>
  );
}

function QuickDate({
  label,
  date,
  active,
  onPick,
}: {
  label: string;
  date: Date;
  active: boolean;
  onPick: (d: Date) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onPick(date)}
      className={
        active
          ? "flex-1 rounded-md bg-brand-600 px-2 py-1.5 text-[11px] font-semibold text-white shadow-sm dark:bg-primary"
          : "flex-1 rounded-md px-2 py-1.5 text-[11px] font-medium text-zinc-600 transition-colors hover:bg-white hover:text-zinc-900 dark:text-muted-foreground dark:hover:bg-card dark:hover:text-foreground"
      }
    >
      {label}
      <span
        className={
          active
            ? "ml-1 text-[9.5px] font-normal opacity-75"
            : "ml-1 text-[9.5px] font-normal text-zinc-400 dark:text-muted-foreground/70"
        }
      >
        {format(date, "dd/MM")}
      </span>
    </button>
  );
}
